import { useCallback, useEffect, useRef, useState } from 'react'
import Sheet from './Sheet.jsx'
import {
  addLinkAttachment, getAttachments, openableUrl, removeAttachment, signMany,
  uploadAttachment,
} from '../lib/attach.js'

/**
 * Files and links on anything — the receipt for a payable, the photo of a
 * quote on a build, the article behind a note.
 *
 * Images show as thumbnails because a wall of filenames tells him nothing;
 * everything else shows as a row with its size. Opening anything mints a
 * signed URL at that moment, so nothing here is a lasting public address.
 */
export default function Attachments({ target, toast, compact }) {
  const [rows, setRows] = useState(null)
  const [thumbs, setThumbs] = useState({})
  const [busy, setBusy] = useState(false)
  const [linking, setLinking] = useState(false)
  const fileRef = useRef(null)

  const load = useCallback(async () => {
    if (!target?.id) { setRows([]); return }
    try {
      const list = await getAttachments(target.type, target.id)
      setRows(list)
      setThumbs(await signMany(list.filter((r) => r.kind === 'image')))
    } catch {
      setRows([])
    }
  }, [target?.type, target?.id])

  useEffect(() => { load() }, [load])

  /* Nothing to attach to before the thing itself exists. */
  if (!target?.id) return null
  if (!rows) return null

  async function onPick(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return

    setBusy(true)
    let added = 0
    for (const f of files) {
      try {
        await uploadAttachment(target.type, target.id, f)
        added += 1
      } catch (err) {
        /* Name the file that failed. "Upload failed" on a batch of five is
           not something he can act on. */
        toast?.(f.name + ': ' + (err.message || 'could not be attached.'))
      }
    }
    setBusy(false)
    if (added) {
      toast?.(added === 1 ? 'Attached.' : added + ' files attached.')
      load()
    }
  }

  async function open(row) {
    try {
      window.open(await openableUrl(row), '_blank', 'noopener')
    } catch (e) {
      toast?.(e.message || 'Could not open that.')
    }
  }

  return (
    <div className={'atts' + (compact ? ' compact' : '')}>
      {rows.length ? (
        <>
          <p className="lbl" style={{ margin: '0 0 6px' }}>Attached</p>
          <div className="attgrid">
            {rows.map((r) => (
              <figure className="att" key={r.id}>
                <button type="button" className="attopen" onClick={() => open(r)}>
                  {r.kind === 'image' && thumbs[r.id] ? (
                    <img src={thumbs[r.id]} alt={r.label || 'Attachment'} loading="lazy" />
                  ) : (
                    <span className="atticon" aria-hidden="true">
                      {r.kind === 'link' ? '↗' : r.kind === 'video' ? '▶' : '¶'}
                    </span>
                  )}
                </button>
                <figcaption>
                  <span className="attname">{r.label}</span>
                  {r.bytes ? <span className="lbl">{readable(r.bytes)}</span> : null}
                </figcaption>
                <button
                  type="button"
                  className="attx"
                  aria-label={'Remove ' + (r.label || 'attachment')}
                  onClick={async () => {
                    try {
                      await removeAttachment(r)
                      load()
                    } catch (e) {
                      toast?.(e.message || 'Could not remove that.')
                    }
                  }}
                >
                  ✕
                </button>
              </figure>
            ))}
          </div>
        </>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        onChange={onPick}
        accept="image/*,video/*,.pdf,.csv,.txt,.doc,.docx,.xls,.xlsx"
      />
      <div className="attadd">
        <button type="button" className="linkish" disabled={busy}
          onClick={() => fileRef.current?.click()}>
          {busy ? 'Attaching…' : rows.length ? 'Attach another' : 'Attach a file'}
        </button>
        <button type="button" className="linkish" onClick={() => setLinking(true)}>
          Paste a link
        </button>
      </div>

      {linking ? (
        <LinkSheet
          target={target}
          onClose={() => setLinking(false)}
          onDone={() => { setLinking(false); load() }}
          toast={toast}
        />
      ) : null}
    </div>
  )
}

function readable(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function LinkSheet({ target, onClose, onDone, toast }) {
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <Sheet title="Paste a link" onClose={onClose}>
      <div className="field">
        <span className="lbl">Address</span>
        <input
          type="url"
          inputMode="url"
          placeholder="https://"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoComplete="off"
        />
      </div>
      <div className="field">
        <span className="lbl">Call it</span>
        <input
          type="text"
          placeholder="Optional"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoComplete="off"
        />
      </div>
      <button
        type="button"
        className="primary"
        disabled={busy || !url.trim()}
        onClick={async () => {
          setBusy(true)
          try {
            await addLinkAttachment(target.type, target.id, url, label)
            onDone()
          } catch (e) {
            toast?.(e.message || 'Could not save that link.')
            setBusy(false)
          }
        }}
      >
        Attach
      </button>
    </Sheet>
  )
}
